import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { fetchCashAccounts } from "../../core/redux/slices/cashSlice";
import { getCashLedgerReport } from "../../core/api/reportService";
import { exportToCsv } from "../../core/utils/exportCsv";
import { getApiErrorMessage } from "../../core/utils/apiError";

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
const dateOnly = (iso) => (iso ? iso.split("T")[0] : "");

// GET /api/reports/cash-ledger?fromDate=&toDate= (HANDOVER_FRONTEND 4.md §9) — the Bank
// Report's shape minus the bankId param, since there's only one cash pool to select from.
// Every Cash-mode/Cash-type entry (invoice payments, income, expense, supplier advance,
// supplier bill payment) is included automatically and retroactively; journal entries are
// excluded because they carry no payment type at all, same as the Bank ledger.
//
// Same opening-balance technique as BankReport.jsx: the endpoint's running `balance` is
// always seeded from the Cash Master opening balance for whatever range is queried, so
// querying just [dateFrom, dateTo] would ignore everything before dateFrom. Pull from a
// fixed epoch through dateTo instead and read the true opening balance off the last row
// strictly before dateFrom, falling back to the Cash Master figure when there's no
// history yet.
const LEDGER_EPOCH = "2000-01-01";

const CashReport = () => {
  const dispatch = useDispatch();
  const cashAccounts = useSelector((s) => s.cash.list);

  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [rows, setRows] = useState(null);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => { dispatch(fetchCashAccounts()); }, [dispatch]);

  // Matches the server's own seeding rule: sum of openingBalance across Active rows.
  const masterOpeningBalance = cashAccounts
    .filter((c) => (c.status || "Active") === "Active")
    .reduce((sum, c) => sum + Number(c.openingBalance || 0), 0);

  const runReport = async () => {
    setLoading(true);
    try {
      const res = await getCashLedgerReport({ from: LEDGER_EPOCH, to: dateTo });
      const allRows = res.data || [];
      const priorRows = allRows.filter((r) => dateOnly(r.date) < dateFrom);
      const inRange = allRows
        .filter((r) => dateOnly(r.date) >= dateFrom)
        .map((r, idx) => ({ ...r, slNo: idx + 1 }));
      setOpeningBalance(priorRows.length ? Number(priorRows[priorRows.length - 1].balance) : masterOpeningBalance);
      setRows(inRange);
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load the cash report."), "error");
    } finally {
      setLoading(false);
    }
  };

  const closingBalance = rows && rows.length ? Number(rows[rows.length - 1].balance) : openingBalance;
  const totals = (rows || []).reduce((t, r) => ({
    in: t.in + Number(r.in || 0),
    out: t.out + Number(r.out || 0),
  }), { in: 0, out: 0 });

  const handleExport = () => {
    const headers = ["Sl.No", "Date", "Particulars", "Accounts Head", "Remarks", "In", "Out", "Balance"];
    const dataRows = rows.map((r) => [
      r.slNo, dateOnly(r.date), r.particulars || "", r.accountsHead || "", r.remarks || "", r.in, r.out, r.balance,
    ]);
    dataRows.unshift(["", "", "", "", "Opening Balance", "", "", openingBalance]);
    dataRows.push(["", "", "", "", "Closing Balance", totals.in, totals.out, closingBalance]);
    exportToCsv(`Cash-Report-${dateFrom}_to_${dateTo}.csv`, headers, dataRows);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Cash Report</h4><h6>Running ledger of cash received and paid out</h6></div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">From</label>
                <input type="date" className="form-control form-control-sm" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">To</label>
                <input type="date" className="form-control form-control-sm" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={runReport} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-outline-success" onClick={handleExport} disabled={!rows || rows.length === 0}>
                  Export to Excel
                </button>
              </div>
            </div>
          </div>
        </div>

        {rows && (
          <>
            <div className="row mb-3">
              <div className="col-6 col-md-3 mb-2">
                <div className="card border-secondary text-center py-2">
                  <div className="text-secondary fs-6 fw-bold">{fmt(openingBalance)}</div>
                  <div className="small text-muted">Opening Balance</div>
                </div>
              </div>
              <div className="col-6 col-md-3 mb-2">
                <div className="card border-success text-center py-2">
                  <div className="text-success fs-6 fw-bold">{fmt(totals.in)}</div>
                  <div className="small text-muted">Cash In</div>
                </div>
              </div>
              <div className="col-6 col-md-3 mb-2">
                <div className="card border-danger text-center py-2">
                  <div className="text-danger fs-6 fw-bold">{fmt(totals.out)}</div>
                  <div className="small text-muted">Cash Out</div>
                </div>
              </div>
              <div className="col-6 col-md-3 mb-2">
                <div className={`card border-${closingBalance >= 0 ? "success" : "danger"} text-center py-2`}>
                  <div className={`text-${closingBalance >= 0 ? "success" : "danger"} fs-6 fw-bold`}>{fmt(closingBalance)}</div>
                  <div className="small text-muted">Closing Balance</div>
                </div>
              </div>
            </div>

            <div className="alert alert-warning py-2 px-3 small">
              Journal entries are not included above &mdash; a journal entry carries no payment type, so it
              can&apos;t be classified as cash in or cash out.
            </div>

            <div className="card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Sl.No</th>
                        <th>Date</th>
                        <th>Particulars</th>
                        <th>Accounts Head</th>
                        <th>Remarks</th>
                        <th className="text-end">In</th>
                        <th className="text-end">Out</th>
                        <th className="text-end">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="table-light">
                        <td colSpan={7} className="text-end fw-semibold">Opening Balance</td>
                        <td className="text-end fw-bold">{fmt(openingBalance)}</td>
                      </tr>
                      {rows.length === 0 ? (
                        <tr><td colSpan={8} className="text-center text-muted py-3">No cash transactions in this date range.</td></tr>
                      ) : rows.map((r) => (
                        <tr key={r.slNo}>
                          <td>{r.slNo}</td>
                          <td>{r.date ? new Date(r.date).toLocaleDateString("en-IN") : "—"}</td>
                          <td>{r.particulars || "—"}</td>
                          <td>{r.accountsHead || "—"}</td>
                          <td>{r.remarks || "—"}</td>
                          <td className="text-end">{r.in ? fmt(r.in) : "—"}</td>
                          <td className="text-end">{r.out ? fmt(r.out) : "—"}</td>
                          <td className="text-end fw-semibold">{fmt(r.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {!rows && !loading && (
          <div className="card text-center py-5 text-muted">
            <p>Select a date range and click <strong>Search</strong> to view the cash ledger.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CashReport;
